import {Component, inject, OnInit, signal, ChangeDetectionStrategy} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {from} from "rxjs"
import {RouterOutlet} from "@angular/router"
import {CreateItemComponent} from "@common/components/base/create-item.component"
import {AddMaterialDialogComponent} from "@platform/components/materials/add-material-dialog/add-material-dialog.component"
import {CheckboxesFilterComponent} from "@common/components/filters/checkboxes-filter/checkboxes-filter.component"
import {ColorCircleFilterComponent} from "@common/components/filters/color-circle-filter/color-circle-filter.component"
import {PinnedFilterOptionComponent} from "@common/components/filters/pinned-filter-option/pinned-filter-option.component"
import {PinnedFilterComponent} from "@common/components/filters/pinned-filter/pinned-filter.component"
import {TagSearchFilterComponent} from "@common/components/filters/tag-search-filter/tag-search-filter.component"
import {ItemFiltersComponent} from "@common/components/item/item-filters/item-filters.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {MaterialsPageScannerUsersGQL, MaterialsPageVisibleUsersGQL} from "@pages/materials/materials-page.generated"
import {SidebarLayoutComponent} from "@platform/components/layouts/sidebar-layout/sidebar-layout.component"
import {Enums} from "@enums"
import {Labels, StateLabel} from "@labels"
import {MaterialsGridComponent} from "@platform/components/materials/materials-grid/materials-grid.component"
import {NativeCheckboxComponent} from "@common/components/inputs/native/native-checkbox/native-checkbox.component"
import {FiltersService} from "@common/services/filters/filters.service"
import {IsDefined} from "@cm/utils/filter"
import {MaterialColorLabels} from "@app/platform/models/state-labels/material-color-labels"
import {MaterialPatternLabels} from "@app/platform/models/state-labels/material-pattern-labels"
import {MaterialTypeLabels} from "@app/platform/models/state-labels/material-type-labels"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-materials-page",
    imports: [
        SidebarLayoutComponent,
        CheckboxesFilterComponent,
        ColorCircleFilterComponent,
        PinnedFilterComponent,
        TagSearchFilterComponent,
        ItemFiltersComponent,
        PinnedFilterOptionComponent,
        RouterOutlet,
        MaterialsGridComponent,
        NativeCheckboxComponent,
    ],
    templateUrl: "./materials-page.component.html",
    styleUrl: "./materials-page.component.scss",
})
export class MaterialsPageComponent extends CreateItemComponent<AddMaterialDialogComponent> implements OnInit {
    readonly Enums = Enums
    readonly Labels = Labels
    readonly MaterialColorLabels = MaterialColorLabels
    readonly MaterialPatternLabels = MaterialPatternLabels
    readonly MaterialTypeLabels = MaterialTypeLabels

    public readonly $viewMode = signal<"materialLibrary" | undefined>(undefined)
    readonly filters = inject(FiltersService)
    readonly permission = inject(PermissionsService)
    readonly organizations = inject(OrganizationsService)
    $can = this.permission.$to

    readonly scannerUsersGql = inject(MaterialsPageScannerUsersGQL)
    readonly visibleUsersGql = inject(MaterialsPageVisibleUsersGQL)

    readonly $scannerUserLabels = signal<StateLabel<string>[] | null>(null)
    readonly $userLabels = signal<StateLabel<string>[] | null>(null)

    ngOnInit() {
        this.$viewMode.set(this.route.snapshot.data.viewMode)

        from(fetchThrowingErrors(this.scannerUsersGql)({}))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({users}) => {
                this.$scannerUserLabels.set(
                    users.filter(IsDefined).map((user) => ({
                        label: user.name,
                        state: user.id,
                    })),
                )
            })
        from(fetchThrowingErrors(this.visibleUsersGql)({}))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({users}) => {
                this.$userLabels.set(
                    users.filter(IsDefined).map((user) => ({
                        label: user.name,
                        state: user.id,
                    })),
                )
            })
    }

    protected openCreateDialog() {
        const dialogRef = this.dialog.open(AddMaterialDialogComponent, {
            data: {},
        })
        this.setDialogRef(dialogRef)
    }

    protected override handleDialogResult(result: unknown): boolean | void {
        if (result && typeof result === "object" && "createMethod" in result && "item" in result) {
            const typedResult = result as {createMethod: string; item: {id: string}}
            if (typedResult.createMethod === "SINGLE" && typedResult.item?.id) {
                // Navigate to the created material
                this.router.navigate([typedResult.item.id], {
                    relativeTo: this.route,
                    queryParamsHandling: "preserve",
                })
                // Return false to skip default navigation
                return false
            }
        }

        // Return undefined/void for default behavior
    }

    async toggleWithoutScan(scannedByNone: boolean) {
        if (scannedByNone) {
            await this.filters.updateCriteria("scannedByNone", ["1"])
        } else {
            await this.filters.updateCriteria("scannedByNone", [])
        }
    }
}
